import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { AxiosError } from "axios";
import { Toast } from "@/ui-kit";
import { InspectorsRepository } from "@/api/inspectors/repository";
import { InspectorsService } from "@/api/inspectors/service";
import { AbortError, ApiErrorCode } from "@/constants/index";
import type { IServerError } from "@/types/common";
import type { IInspectorW9Form } from "@/types/users-inspectors";
import { W9EntityType } from "@/types/users-inspectors";

type ITaxInformationFormData = {
  w9Name: string;
  w9TIN: string;
  w9SSN: string;
  w9EntityType: string;
  w9File: File[];
  w9FileUrl?: string;
};

type IUseTaxInformationFormOptions = {
  userId: number;
  w9Form?: IInspectorW9Form;
  onSuccess: (response: IInspectorW9Form) => void;
};

export const useTaxInformationForm = ({
  userId,
  w9Form,
  onSuccess,
}: IUseTaxInformationFormOptions) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const schema = useMemo(() => {
    return z
      .object({
        w9Name: z.string().min(1, { message: t("validation.required") }),
        w9TIN: z.string().optional(),
        w9SSN: z.string().optional(),
        w9EntityType: z.string().min(1, { message: t("validation.required") }),
        w9File: z
          .array(z.instanceof(File))
          .refine((files) => files.length > 0, {
            message: t("validation.required"),
          }),
        w9FileUrl: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        // Individual: TIN optional, SSN required
        if (data.w9EntityType === W9EntityType.Individual) {
          if (!data.w9SSN) {
            ctx.addIssue({
              code: "custom",
              message: t("validation.required"),
              path: ["w9SSN"],
            });
          } else if (data.w9SSN.length !== 9) {
            ctx.addIssue({
              code: "custom",
              message: t("validation.pattern_ssn_number"),
              path: ["w9SSN"],
            });
          }
          // TIN is optional but if provided must be 9 digits
          if (data.w9TIN && data.w9TIN.length !== 9) {
            ctx.addIssue({
              code: "custom",
              message: t("validation.pattern_tin_number"),
              path: ["w9TIN"],
            });
          }
        }

        // Corporate: TIN required (labeled as TIN/EIN, value copied to SSN)
        if (data.w9EntityType === W9EntityType.Corporate) {
          if (!data.w9TIN) {
            ctx.addIssue({
              code: "custom",
              message: t("validation.required"),
              path: ["w9TIN"],
            });
          } else if (data.w9TIN.length !== 9) {
            ctx.addIssue({
              code: "custom",
              message: t("validation.pattern_tin_number"),
              path: ["w9TIN"],
            });
          }
        }

        // LLC: Both TIN and SSN required
        if (data.w9EntityType === W9EntityType.LLC) {
          if (!data.w9TIN) {
            ctx.addIssue({
              code: "custom",
              message: t("validation.required"),
              path: ["w9TIN"],
            });
          } else if (data.w9TIN.length !== 9) {
            ctx.addIssue({
              code: "custom",
              message: t("validation.pattern_tin_number"),
              path: ["w9TIN"],
            });
          }
          if (!data.w9SSN) {
            ctx.addIssue({
              code: "custom",
              message: t("validation.required"),
              path: ["w9SSN"],
            });
          } else if (data.w9SSN.length !== 9) {
            ctx.addIssue({
              code: "custom",
              message: t("validation.pattern_ssn_number"),
              path: ["w9SSN"],
            });
          }
        }
      });
  }, [t]);

  const defaultValues = useMemo((): ITaxInformationFormData => {
    return {
      w9Name: w9Form?.name?.toString() ?? "",
      w9TIN: w9Form?.tin?.toString() ?? "",
      w9SSN: w9Form?.ssn?.toString() ?? "",
      w9EntityType: w9Form?.entityType ?? W9EntityType.Individual,
      w9File: [],
      w9FileUrl: w9Form?.frontImageUrl,
    };
  }, [w9Form]);

  const form = useForm<ITaxInformationFormData>({
    defaultValues,
    resolver: zodResolver(schema) as any,
  });

  const { handleSubmit, setError } = form;

  const onSubmit = useCallback(() => {
    handleSubmit((formData: ITaxInformationFormData) => {
      setLoading(true);
      const repository = new InspectorsRepository(new InspectorsService());

      repository
        .upsertW9Details({
          w9Form: {
            userId,
            name: formData.w9Name,
            tin: formData.w9TIN,
            ssn: formData.w9SSN,
            entityType: formData.w9EntityType,
            frontImageUrl: formData.w9FileUrl,
          },
        })
        .then((response) => {
          Toast.success({
            title: t("toast.what_updated", {
              what: t("label.tax_information"),
            }),
          });
          onSuccess(response);
        })
        .catch((error: AxiosError<IServerError>) => {
          if (
            error?.response?.status !== ApiErrorCode.Unauthorized &&
            error?.name !== AbortError
          ) {
            const errorMessage =
              error?.response?.data?.error || error?.message || "";

            if (errorMessage.includes("uq_w9form_tin")) {
              setError("w9TIN", {
                message: t("validation.tin_already_used"),
              });
              Toast.error({ title: t("validation.tin_already_used") });
            } else if (errorMessage.includes("uq_w9form_ssn")) {
              setError("w9SSN", {
                message: t("validation.ssn_already_used"),
              });
              Toast.error({ title: t("validation.ssn_already_used") });
            } else {
              Toast.error({ title: errorMessage });
            }
          }
        })
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [handleSubmit, userId, onSuccess, setError, t]);

  return { form, onSubmit, loading, disabled, setDisabled };
};
